import { useState } from 'react';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import useSWR from 'swr';

import { swrFetcher } from '../../helpers/fetch';
import { DrainDownOption } from './DrainDown.enum';
import PackageCard from './PackageCard';
import styles from './Packages.module.scss';

export type Package = {
  tag: DrainDownOption;
  name: string;
  basePackageName: string;
  basePackagePrice: string;
  description?: string;
  discount: string;
  for: string;
  additionalServices: {
    quantity: number;
    additionalService: {
      tag: string;
      name: string;
      description?: string;
      price: number;
      type: string;
      for: string;
    };
  }[];
  price: string;
  priceDiscount: string;
};

export default function Packages({
  parkCode,
  handleChoice,
}: {
  parkCode: string;
  handleChoice: (value: DrainDownOption, packageOption?: Package) => void;
}) {
  const [hasCentralHeating, setHasCentralHeating] = useState(true);
  const [accomType, setAccomType] = useState<'caravan' | 'lodge'>('caravan');

  const { data, error } = useSWR<Package[], Error>(
    `/draindown/packages/${parkCode}?availableFor=${
      accomType === 'lodge' ? 'lodge' : 'caravan'
    }`,
    swrFetcher
  );

  if (error) {
    return (
      <div className={styles.root}>
        <p>Sorry, we couldn&apos;t load the packages for your accommodation.</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div>
        <p className="owners-body">What type of accommodation do you have?</p>
        <div className={styles.buttons}>
          <Button
            variant={accomType === 'caravan' ? 'contained' : 'outlined'}
            onClick={() => setAccomType('caravan')}
          >
            Caravan
          </Button>
          <Button
            variant={accomType === 'caravan' ? 'outlined' : 'contained'}
            onClick={() => setAccomType('lodge')}
          >
            Lodge
          </Button>
        </div>
      </div>
      {accomType === 'caravan' && (
        <div data-testid="packageOptions__ch">
          <p className="owners-body">Do you have central heating?</p>
          <div className={styles.buttons}>
            <Button
              variant={hasCentralHeating ? 'contained' : 'outlined'}
              onClick={() => setHasCentralHeating(true)}
            >
              Yes
            </Button>
            <Button
              variant={hasCentralHeating ? 'outlined' : 'contained'}
              onClick={() => setHasCentralHeating(false)}
            >
              No
            </Button>
          </div>
        </div>
      )}
      <div className={styles.packagesContainer}>
        {data.length === 0 && (
          <p className="owners-body">
            Sorry, we don&apos;t have any packages available for your chosen
            options.
          </p>
        )}
        {data
          ?.filter(
            // filter out packages that don't have central heating
            (packageOption) => {
              const noCentralHeatingTag =
                packageOption.tag.includes('no_central_heating');
              const isLodge = packageOption.tag.includes('lodge');

              if (isLodge) {
                return true;
              }

              if (hasCentralHeating) {
                return !noCentralHeatingTag;
              } else {
                return noCentralHeatingTag;
              }
            }
          )
          .map((packageOption) => (
            <PackageCard
              key={packageOption.tag}
              name={packageOption.name.split(' ')[0]}
              price={packageOption.price}
              includes={[
                {
                  text: packageOption.basePackageName,
                  price: parseInt(packageOption.basePackagePrice),
                },
                ...packageOption.additionalServices.map((as) => ({
                  text: as.additionalService.name,
                  price: as.additionalService.price,
                  quantity: as.quantity,
                })),
              ]}
              discount={packageOption.discount}
              savings={packageOption.priceDiscount}
              buttons={[
                {
                  text: `Choose ${packageOption.name.split(' ')[0]}`,
                  onClick: () => {
                    handleChoice(packageOption.tag, packageOption);
                  },
                },
              ]}
            />
          ))}
      </div>
    </div>
  );
}
